import { useState, useEffect, useCallback } from "react";
import { Box, Button, Typography, CircularProgress } from "@mui/material";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import CloudSyncRoundedIcon from "@mui/icons-material/CloudSyncRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import PlaylistAddCheckRoundedIcon from "@mui/icons-material/PlaylistAddCheckRounded";
import { formatCurrency } from "../../config/Helper";
import { useTheme } from "@mui/material/styles";
import AppDialog from "../General/Dialog/AppDialog";
import PreviewDialog from "../General/Dialog/PreviewDialog";
import OnlineQueryTooltip, {
  useOnlineQueryGate,
} from "../General/OnlineQueryGate";
import { useToast } from "../../context/ToastContext";
import {
  getTaxReceipts,
  syncTaxReceipts,
  getTaxReceiptDetail,
} from "../../services/IndividualLiquidationService";
import LawyerSessionRequiredDialog from "../General/Dialog/LawyerSessionRequiredDialog";

function fmtDate(v) {
  if (!v) return "—";
  // "YYYY-MM-DD" se formatea directo para no desfasar un día por timezone
  // (new Date("2021-12-09") es UTC y en AR muestra el día anterior).
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
  if (m) return `${+m[3]}/${+m[2]}/${m[1]}`;
  return new Date(v).toLocaleDateString("es-AR");
}

/**
 * Comprobantes de tasa de justicia del expediente (portal judicial).
 * Lista los guardados, permite re-sincronizar desde el portal y ver el HTML
 * de cada comprobante para leer los montos que después se cargan a mano.
 */
export default function TaxReceiptsDialog({ open, onClose, legalFileId, legalFileCode, onApplyRubros }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { showToast } = useToast();

  const { online } = useOnlineQueryGate();
  const [rows, setRows] = useState([]);
  const [synced, setSynced] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [suggestedRubros, setSuggestedRubros] = useState([]);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailHtml, setDetailHtml] = useState(null);
  const [detailTitle, setDetailTitle] = useState("");
  const [sessionRequiredLawyer, setSessionRequiredLawyer] = useState(null);

  const load = useCallback(async () => {
    if (!legalFileId) return;
    setLoading(true);
    try {
      const res = await getTaxReceipts(legalFileId);
      setRows(res.data?.data ?? []);
      setSynced(!!res.data?.synced);
      setSuggestedRubros(res.data?.suggested_rubros ?? []);
    } catch {
      showToast("No se pudieron cargar los comprobantes de tasa.", "error");
    } finally {
      setLoading(false);
    }
  }, [legalFileId, showToast]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await syncTaxReceipts(legalFileId);
      setRows(res.data?.data ?? []);
      setSynced(true);
      setSuggestedRubros(res.data?.suggested_rubros ?? []);
      if (res.data?.message) {
        showToast(res.data.message, "info");
      } else {
        showToast(`${(res.data?.data ?? []).length} comprobante(s) sincronizado(s).`, "success");
      }
    } catch (e) {
      if (e?.response?.data?.code === "judicial_session_required") {
        setSessionRequiredLawyer(e.response.data.lawyer || {});
      } else {
        showToast(
          e?.response?.data?.message ??
            "No se pudieron consultar los comprobantes de tasa en el portal judicial.",
          "error",
        );
      }
    } finally {
      setSyncing(false);
    }
  };

  const openDetail = async (row) => {
    setDetailTitle(`Comprobante N° ${row.numero_comprobante ?? ""}`);
    setDetailHtml(null);
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const res = await getTaxReceiptDetail(legalFileId, row.id);
      setDetailHtml(res.data?.detalle ?? null);
    } catch (e) {
      setDetailOpen(false);
      showToast(e?.response?.data?.message ?? "No se pudo abrir el comprobante.", "error");
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <>
      <LawyerSessionRequiredDialog
        open={Boolean(sessionRequiredLawyer)}
        onClose={() => setSessionRequiredLawyer(null)}
        lawyer={sessionRequiredLawyer}
        onCaptured={() => handleSync()}
      />

      <AppDialog
        open={open}
        onClose={onClose}
        title="Comprobantes de tasa de justicia"
        subtitle={legalFileCode ? `Expediente ${legalFileCode} · portal judicial` : "Portal judicial"}
        icon={<ReceiptLongRoundedIcon />}
        maxWidth="sm"
        fullWidth
        stickyHeader
        actions={
          <>
            <OnlineQueryTooltip show={!online}>
              <Button
                onClick={handleSync}
                disabled={syncing || loading || !online}
                startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <CloudSyncRoundedIcon />}
                sx={{ mr: "auto", borderRadius: 2, fontWeight: 800 }}
              >
                {syncing ? "Consultando…" : "Actualizar desde el portal"}
              </Button>
            </OnlineQueryTooltip>
            <Button onClick={onClose} disabled={syncing} sx={{ borderRadius: 2 }}>
              Cerrar
            </Button>
          </>
        }
      >
        {onApplyRubros && suggestedRubros.length === 3 && (
          <Box
            sx={{
              mb: 1.5,
              px: 1.5,
              py: 1.25,
              borderRadius: 2,
              border: "1px solid",
              borderColor: isDark ? "rgba(46,125,50,0.4)" : "rgba(46,125,50,0.35)",
              background: isDark ? "rgba(46,125,50,0.10)" : "rgba(46,125,50,0.06)",
            }}
          >
            <Typography sx={{ fontWeight: 800, fontSize: 13, mb: 0.5 }}>
              Cargar rubros de gastos desde el comprobante
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.2, mb: 1 }}>
              {suggestedRubros.map((r, i) => (
                <Typography key={i} sx={{ fontSize: 11.5, opacity: 0.75 }}>
                  {r.name} · {formatCurrency(r.fixed_amount)}
                  {r.from ? ` · desde ${fmtDate(r.from)}` : ""}
                </Typography>
              ))}
            </Box>
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<PlaylistAddCheckRoundedIcon sx={{ fontSize: 17 }} />}
              onClick={() => onApplyRubros(suggestedRubros)}
              sx={{ borderRadius: 2, fontWeight: 800, textTransform: "none" }}
            >
              Usar montos en la planilla
            </Button>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4, px: 2 }}>
            <ReceiptLongRoundedIcon sx={{ fontSize: 34, opacity: 0.25, mb: 1 }} />
            <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
              {synced ? "Sin comprobantes de tasa" : "Todavía no se consultaron"}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.65 }}>
              {synced
                ? "Este expediente no tiene comprobantes de tasa cargados en el portal judicial."
                : "Usá «Actualizar desde el portal» para traer los comprobantes de tasa de este expediente."}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {rows.map((row) => (
              <Box
                key={row.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 1.5,
                  py: 1.2,
                  borderRadius: 2,
                  border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
                  background: isDark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.6)",
                }}
              >
                <ReceiptLongRoundedIcon sx={{ fontSize: 20, color: "primary.main", opacity: 0.8 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
                    N° {row.numero_comprobante ?? "—"}
                  </Typography>
                  <Typography sx={{ fontSize: 12, opacity: 0.6 }}>
                    Cobrado el {fmtDate(row.fecha_cobro)}
                  </Typography>
                </Box>
                <OnlineQueryTooltip show={!online}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<VisibilityRoundedIcon sx={{ fontSize: 16 }} />}
                    onClick={() => openDetail(row)}
                    disabled={!online}
                    sx={{ borderRadius: 2, fontWeight: 800, flexShrink: 0 }}
                  >
                    Ver comprobante
                  </Button>
                </OnlineQueryTooltip>
              </Box>
            ))}
            <Typography sx={{ fontSize: 11, opacity: 0.5, mt: 0.5 }}>
              Los montos se leen del comprobante y se cargan a mano en los rubros de la liquidación.
            </Typography>
          </Box>
        )}
      </AppDialog>

      <PreviewDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        title={detailTitle}
        subtitle="Comprobante de tasa · portal judicial"
        html={detailHtml}
        loading={detailLoading}
      />
    </>
  );
}
